import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import muiPropForwarding from 'styles/helpers/muiPropForwarding';
import VisuallyHiddenLink from 'components/VisuallyHiddenLink';
import {
  CartItems,
  CartOrderMinimumAlert,
  CartFulfillmentMethod,
  MenuUpsell
} from 'catering/components/Cart';
import CartInfo from 'catering/components/Cart/CartInfo';
import CartHeader from 'components/Cart/CartHeader';
import OutlinedBoxError from 'components/OutlinedBoxError';
import { getDateTimeFromFulfillmentDateAndTime } from 'catering/helpers';
import { getValueWithTwoDecimalPlace } from 'helpers/formattedDollar';

import { styled } from '@mui/system';
import { getPageBackgroundColor } from 'styles/helpers/getThemeStyles';
import { useLayout } from 'bento-ordering/context/LayoutContext';

const CheckoutCtaAndErrorsWrapper = styled(Box)(({ theme }) => ({
  flexDirection: 'column',
  backgroundColor: getPageBackgroundColor(theme),
  width: '25rem',
  padding: '20px'
}));

const StyledCart = styled('div', {
  shouldForwardProp: muiPropForwarding('bottomNavHeight')
})(({ bottomNavHeight }) => ({
  paddingBottom: `${bottomNavHeight || 0}px`,
  overflowX: 'hidden'
}));
function Cart({
  cart,
  checkoutAction,
  onEditCartItem,
  onFulfillmentMethodChange,
  onOpenEmptyCartConfirmation,
  onRemoveCartItem,
  validationError = null
}) {
  const {
    bundle_catering_service_fee_with_custom_fee,
    bundled_catering_service_and_custom_fees,
    catering_custom_fee,
    catering_custom_fee_name,
    catering_delivery_fee,
    catering_pickup_fee,
    catering_fulfillment_datetime_range_local,
    catering_fulfillment_method,
    catering_service_fee,
    catering_taxes_and_fees,
    fulfillment_date,
    fulfillment_time,
    items,
    min_order_amount,
    promotion,
    shipping_street_address_1,
    shipping_street_address_2,
    shipping_city,
    shipping_state,
    shipping_postal_code,
    subtotal,
    tax,
    total, // TODO: replace it with total_owed
    should_show_transparent_fees,
    display_service_fee
  } = cart;

  const { bottomNavHeight } = useLayout();

  const getFulfillmentAddress = () => {
    const streetAddress2 = shipping_street_address_2
      ? `${shipping_street_address_2}, `
      : '';
    return `${shipping_street_address_1}, ${streetAddress2}${shipping_city}, ${shipping_state} ${shipping_postal_code}`;
  };

  const fulfillmentDateTime = getDateTimeFromFulfillmentDateAndTime(
    fulfillment_date,
    fulfillment_time
  );

  const taxLabel = catering_taxes_and_fees ? 'Tax & Fees' : 'Tax';
  const taxesAndFees = catering_taxes_and_fees
    ? getValueWithTwoDecimalPlace(catering_taxes_and_fees)
    : null;

  return (
    <StyledCart data-testid="cart" bottomNavHeight={bottomNavHeight}>
      <Box padding={2.5}>
        <CartHeader
          fulfillmentMethod={catering_fulfillment_method}
          onClick={onFulfillmentMethodChange}
        />

        <VisuallyHiddenLink href="#cart-checkout-button">
          Skip to checkout
        </VisuallyHiddenLink>

        <CartFulfillmentMethod
          fulfillmentAddress={getFulfillmentAddress()}
          fulfillmentDateTime={fulfillmentDateTime}
          fulfillmentTimeRange={catering_fulfillment_datetime_range_local}
          fulfillmentMethod={catering_fulfillment_method}
          onFulfillmentMethodChange={onFulfillmentMethodChange}
        />

        <CartItems
          items={items}
          onEditCartItem={onEditCartItem}
          onRemoveCartItem={onRemoveCartItem}
        />

        <CartInfo
          bundleServiceFeeWithCustomFee={
            bundle_catering_service_fee_with_custom_fee
          }
          bundledServiceAndCustomFees={bundled_catering_service_and_custom_fees}
          toggleIsOpen={onOpenEmptyCartConfirmation}
          taxFieldLabel={taxLabel}
          customFee={catering_custom_fee}
          customFeeName={catering_custom_fee_name}
          deliveryFee={catering_delivery_fee}
          fulfillmentMethod={catering_fulfillment_method}
          pickupFee={catering_pickup_fee}
          promotion={promotion}
          serviceFee={catering_service_fee}
          subtotal={subtotal}
          tax={tax}
          taxesAndFees={taxesAndFees}
          totalOwed={total}
          shouldShowTransparentFees={should_show_transparent_fees}
          displayServiceFee={display_service_fee}
        />

        <CartOrderMinimumAlert
          min_order_amount={min_order_amount}
          subtotal={subtotal}
        />
      </Box>
      <MenuUpsell />
      <CheckoutCtaAndErrorsWrapper>
        <Box marginBottom={2.5}>{checkoutAction}</Box>
        {validationError && (
          <OutlinedBoxError>{validationError}</OutlinedBoxError>
        )}
      </CheckoutCtaAndErrorsWrapper>
    </StyledCart>
  );
}

Cart.propTypes = {
  cart: PropTypes.object.isRequired,
  checkoutAction: PropTypes.element.isRequired,
  onEditCartItem: PropTypes.func.isRequired,
  onFulfillmentMethodChange: PropTypes.func.isRequired,
  onOpenEmptyCartConfirmation: PropTypes.func.isRequired,
  onRemoveCartItem: PropTypes.func.isRequired,
  validationError: PropTypes.string,
  should_show_transparent_fees: PropTypes.bool.isRequired
};

export default Cart;
